import { TabConfig } from '@components/tabs/BaseEnhancedTabs';
import { CameraTask, SpecificTaskTypes, TaskPriority, TaskStatus } from '@EcamModel/model/CameraTask';
import { BaseSelect } from '@pages/cameras/list';
import color from '@theme/Colors';
import { ViewTaskTab } from '../components/popup/PopupViewCompleteTask';
import { BaseHeadProps } from '@components/tables/TableCellSort';
import { appConfig } from '@configs/index';
import { ListProps } from '@Core';
import { CameraPriority } from '@EcamModel/model';
import { CameraTaskAttachment } from '@EcamModel/model/CameraTaskAttachment';
import { ZonePriority } from '@EcamModel/model/Zones';
import { SxProps } from '@mui/material';
import { Theme } from '@mui/system';
import { CSSProperties } from 'react';

type Normal<S extends string> = S;

export type AsTypeSort<T> = {
    [P in keyof T as `-${Normal<string & P>}`]: T[P];
};

export type OwnFilterCameraTask = ListProps<CameraTask>;

export interface AttachmentsFile {
    id: number;
    description?: string;
    blobName: string;
}

export interface AttachmentsFiles extends Partial<CameraTaskAttachment> {
    _fakeId?: string;
    fileUrl: File;
}

export const MAX_FILES = 12;
export const MAX_SIZE_MB = 10;
export const blobURLCameraAttachmentsPhoto = appConfig.gateway.cameraTaskAttachmentBlobURL;

export enum TypeHandleConfirm {
    Status,
    Priority,
    Delete,
}

export interface HandleConfirmParams {
    type: TypeHandleConfirm;
    task: CameraTask;
    selectedAttribute?: TaskStatus | TaskPriority;
    onSuccessMessage: string;
    onClosePopup: () => void;
}

export enum DropdownType {
    Status = 'status',
    Priority = 'priority',
}

export enum SubTaskStatus {
    Todo = 'To do',
    Done = 'Done',
}

export interface CameraSubTasks {
    Name: SpecificTaskTypes;
    Status: SubTaskStatus;
    CompletedAt: Date | null;
}

export const mappingStatusTask: Record<TaskStatus, string> = {
    [TaskStatus.Planned]: 'Planned',
    [TaskStatus.Allocated]: 'Allocated',
    [TaskStatus.InProgress]: 'In Progress',
    [TaskStatus.Completed]: 'Completed',
    [TaskStatus.Cancelled]: 'Cancelled',
};

export const mappingPriorityTask: Record<TaskPriority, string> = {
    [TaskPriority.Low]: 'Low',
    [TaskPriority.Medium]: 'Medium',
    [TaskPriority.High]: 'High',
    [TaskPriority.Critical]: 'Critical',
};

export const mappingPriorityZone: Record<ZonePriority, string> = {
    [ZonePriority.P0]: 'P0',
    [ZonePriority.P1]: 'P1',
    [ZonePriority.P2]: 'P2',
    [ZonePriority.P3]: 'P3',
    [ZonePriority.P4]: 'P4',
    [ZonePriority.P5]: 'P5',
    [ZonePriority.P6]: 'P6',
};

export const mappingPriorityCamera: Record<CameraPriority, string> = {
    [TaskPriority.Low]: 'Low',
    [TaskPriority.Medium]: 'Medium',
    [TaskPriority.High]: 'High',
};

export enum SearchType {
    Task,
    Camera,
    Assignee,
    CreatedBy,
}

export const searchTypes: BaseSelect[] = [
    {
        Id: 1,
        Name: 'Task',
        Value: SearchType.Task,
    },
    {
        Id: 2,
        Name: 'Camera',
        Value: SearchType.Camera,
    },
    {
        Id: 3,
        Name: 'Assignee',
        Value: SearchType.Assignee,
    },
    {
        Id: 4,
        Name: 'Create by',
        Value: SearchType.CreatedBy,
    },
];

export const searchTypeMapping: { [key in SearchType]?: Array<keyof CameraTask> } = {
    [SearchType.Task]: ['TaskName'],
    [SearchType.Assignee]: ['Assignee'],
    [SearchType.CreatedBy]: ['CreatedBy'],
};

export const cameraStatusTask: BaseSelect[] = [
    {
        Id: 1,
        Name: mappingStatusTask[TaskStatus.Planned],
        Value: TaskStatus.Planned,
    },
    {
        Id: 2,
        Name: mappingStatusTask[TaskStatus.Allocated],
        Value: TaskStatus.Allocated,
    },
    {
        Id: 3,
        Name: mappingStatusTask[TaskStatus.InProgress],
        Value: TaskStatus.InProgress,
    },
    {
        Id: 4,
        Name: mappingStatusTask[TaskStatus.Completed],
        Value: TaskStatus.Completed,
    },
    {
        Id: 5,
        Name: mappingStatusTask[TaskStatus.Cancelled],
        Value: TaskStatus.Cancelled,
    },
];

export const cameraPriorityTask: BaseSelect[] = [
    {
        Id: 1,
        Name: mappingPriorityTask[TaskPriority.Low],
        Value: TaskPriority.Low,
    },
    {
        Id: 2,
        Name: mappingPriorityTask[TaskPriority.Medium],
        Value: TaskPriority.Medium,
    },
    {
        Id: 3,
        Name: mappingPriorityTask[TaskPriority.High],
        Value: TaskPriority.High,
    },
    // {
    //     Id: 4,
    //     Name: mappingPriorityTask[TaskPriority.Critical],
    //     Value: TaskPriority.Critical,
    // },
];

export interface DropdownTypeStyles {
    [key: string]: { backgroundColor: string; color: string; hoverColor?: string };
}

export const statusStyles: { [key in TaskStatus]: { backgroundColor: string; color: string; hoverColor?: string } } = {
    [TaskStatus.Planned]: { backgroundColor: color.grey200, color: color.textPrimary, hoverColor: color.grey300 },
    [TaskStatus.Allocated]: { backgroundColor: '#E0F7FA', color: '#006064', hoverColor: '#D0F0F5' },
    [TaskStatus.InProgress]: { backgroundColor: '#007BFF1A', color: color.warning, hoverColor: '#007BFF3A' },
    [TaskStatus.Completed]: { backgroundColor: color.lightPrimary, color: color.priText },
    [TaskStatus.Cancelled]: { backgroundColor: '#FFE7E6', color: '#E01B00' },
};

export const priorityStyles: {
    [key in TaskPriority]: { backgroundColor: string; color: string; hoverColor?: string };
} = {
    [TaskPriority.Low]: { backgroundColor: color.grey200, color: color.textPrimary, hoverColor: color.grey300 },
    [TaskPriority.Medium]: { backgroundColor: '#FFFBE6', color: '#FAAD14', hoverColor: '#FFF8CC' },
    [TaskPriority.High]: { backgroundColor: '#FFF1F0', color: '#E01B00', hoverColor: '#FFE7E6' },
    [TaskPriority.Critical]: { backgroundColor: '#FFF1F0', color: '#E01B00', hoverColor: '#FFE7E6' },
};

export const stylesMapping: { [key in DropdownType]: DropdownTypeStyles } = {
    [DropdownType.Status]: statusStyles,
    [DropdownType.Priority]: priorityStyles,
};

export const cameraSubTask: CameraSubTasks[] = [
    { Status: SubTaskStatus.Todo, Name: SpecificTaskTypes.AngleDeviation, CompletedAt: null },
    { Status: SubTaskStatus.Todo, Name: SpecificTaskTypes.BatteryHealth, CompletedAt: null },
    { Status: SubTaskStatus.Todo, Name: SpecificTaskTypes.SDCard, CompletedAt: null },
    { Status: SubTaskStatus.Todo, Name: SpecificTaskTypes.ChangeSIMCard, CompletedAt: null },
    { Status: SubTaskStatus.Todo, Name: SpecificTaskTypes.ReplaceBattery, CompletedAt: null },
    { Status: SubTaskStatus.Todo, Name: SpecificTaskTypes.ReplaceOrSwapCamera, CompletedAt: null },
    { Status: SubTaskStatus.Todo, Name: SpecificTaskTypes.ReplaceSDCard, CompletedAt: null },
    { Status: SubTaskStatus.Todo, Name: SpecificTaskTypes.LowCaptureInvestigation, CompletedAt: null },
    { Status: SubTaskStatus.Todo, Name: SpecificTaskTypes.ExcessiveCaptureInvestigation, CompletedAt: null },
];

export const taskTypeToTabMap: Partial<Record<SpecificTaskTypes, TabConfig>> = {
    [SpecificTaskTypes.AngleDeviation]: { label: 'Check deviation', index: ViewTaskTab.Deviation },
    [SpecificTaskTypes.BatteryHealth]: { label: 'Check battery', index: ViewTaskTab.Battery },
    [SpecificTaskTypes.SDCard]: { label: 'Check SD card', index: ViewTaskTab.SdCard },
};
export const stickiesStyle = ({ style }: { style: SxProps<Theme> }): SxProps<Theme> => ({
    position: { md: 'sticky' },
    background: '#fff',
    zIndex: 1,
    ...style,
});

export const columnsHeadCameraTask: BaseHeadProps<CameraTask>[] = [
    {
        title: 'No.',
        align: 'left',
        sx: stickiesStyle({
            style: { minWidth: '50px', left: 0, zIndex: 5 },
        }),
    },

    {
        title: 'Camera',
        align: 'left',
        sx: stickiesStyle({
            style: { minWidth: 'max-content', left: '57px', zIndex: 5 },
        }),
        sortKey: 'CameraName',
        isLeftLabel: true,
    },
    {
        title: 'Camera priority',
        align: 'left',
        sortKey: 'CameraPriority',
    },
    {
        title: 'Task',
        align: 'left',
        sortKey: 'TaskName',
        isLeftLabel: true,
    },

    { title: 'Sub task', align: 'left', sx: { minWidth: '120px' } },

    { title: 'Location', align: 'left', sortKey: 'LocationName', isLeftLabel: true },
    { title: 'Zone', align: 'left', sortKey: 'ZoneName', isLeftLabel: true },
    { title: 'Zone priority', align: 'left', sortKey: 'ZonePriority' },
    { title: 'Status', align: 'center', sortKey: 'TaskStatus', sx: { minWidth: '100px' } },
    { title: 'Priority', align: 'center', sortKey: 'TaskPriority', sx: { minWidth: '100px' } },

    { title: 'Assign to', align: 'left', sx: { minWidth: '120px' } },
    { title: 'Created by', align: 'left', sx: { minWidth: '150px' } },
    { title: 'Start date', align: 'center', sx: { minWidth: '120px' } },
    { title: 'Completed date', align: 'center', sx: { minWidth: '160px' } },
    { title: 'Fault date', align: 'center', sx: { minWidth: '120px' } },
    { title: 'Attachments', align: 'center', sx: { width: 'max-content' } },

    {
        title: 'Due date',
        align: 'center',
        sortKey: 'EndDate',
        sx: {
            position: { md: 'sticky' },
            right: 168,
            background: '#fff',
            zIndex: 5,
        },
    },

    // { title: 'Problem', align: 'center' },
    // { title: 'Solution', align: 'center' },
    {
        title: 'Actions',
        align: 'center',
        sx: stickiesStyle({ style: { minWidth: '168px', right: 0 } }),
    },
];
